/**
 * Server-side locale resolution from the `Accept-Language` header (S010; REQ-API-004, conflict C-13).
 *
 * There is deliberately **no** built-in default locale. The caller supplies the `supported` set and a
 * required `fallback` (both come from the resolved country/market pack), so the platform never silently
 * assumes one country's language — the exact defect C-13 flagged. Matching is a standard `Accept-Language`
 * negotiation: quality-ordered, case-insensitive, with a primary-subtag fallback (a region-tagged request
 * matches a language-only offering and vice-versa).
 */
/** Parse an `Accept-Language` value into quality-ordered tags (stable for equal q). */
function parseAcceptLanguage(header) {
    const ranked = [];
    header.split(',').forEach((part, index) => {
        const [tagRaw, ...params] = part.trim().split(';');
        const tag = tagRaw?.trim();
        if (!tag)
            return;
        let q = 1;
        for (const p of params) {
            const m = /^q=(\d(?:\.\d{1,3})?)$/.exec(p.trim());
            if (m)
                q = Number(m[1]);
        }
        // keep original order as a tiebreaker by nudging q by index
        ranked.push({ tag, q: q - index * 1e-6 });
    });
    return ranked.filter((r) => r.q > 0).sort((a, b) => b.q - a.q);
}
const primary = (tag) => tag.toLowerCase().split('-')[0] ?? tag.toLowerCase();
/**
 * Resolve the best supported locale for an `Accept-Language` header. Returns the `fallback` (with
 * `matched: false`) when the header is missing, empty, `*`, or names no supported language.
 */
export function resolveLocale(acceptLanguage, opts) {
    const { supported, fallback } = opts;
    if (!acceptLanguage || acceptLanguage.trim() === '') {
        return { locale: fallback, matched: false };
    }
    const supportedByLower = new Map(supported.map((s) => [s.toLowerCase(), s]));
    const supportedByPrimary = new Map();
    for (const s of supported) {
        const p = primary(s);
        if (!supportedByPrimary.has(p))
            supportedByPrimary.set(p, s);
    }
    for (const { tag } of parseAcceptLanguage(acceptLanguage)) {
        if (tag === '*')
            continue;
        const exact = supportedByLower.get(tag.toLowerCase());
        if (exact)
            return { locale: exact, matched: true };
        const byPrimary = supportedByPrimary.get(primary(tag));
        if (byPrimary)
            return { locale: byPrimary, matched: true };
    }
    return { locale: fallback, matched: false };
}
/**
 * Resolve the display locale for a request in a country context: negotiate `acceptLanguage` against the
 * country's supported locales, falling back to the country's default locale. A thin tie between
 * `resolveLocale` and a country pack's locale configuration. `Country != Locale`: the result is chosen from
 * what the country *serves*, never inferred from the country code — a country may serve many locales, and a
 * request may match any of them.
 */
export function resolveCountryLocale(config, acceptLanguage) {
    return resolveLocale(acceptLanguage, {
        supported: config.supportedLocales,
        fallback: config.defaultLocale,
    });
}
//# sourceMappingURL=locale.js.map